import {
  AtSign,
  BadgeCheck,
  Camera,
  Cloud,
  Gamepad2,
  Ghost,
  Hash,
  MessageCircle,
  MessageSquare,
  Music,
  Pin,
  Send,
  Share2,
  Star,
  ThumbsUp,
  X as XIcon,
} from "lucide-react";
import { forwardRef } from "react";

// A lucide tirou os ícones de marca (Twitter, Youtube e afins) — então aqui
// implementamos glifos (símbolos) SEM FUNDO, para seguir EXATAMENTE o padrão
// visual das outras redes do sistema (MessageCircle verde WhatsApp, Send
// azul Telegram, ThumbsUp azul Facebook etc.). O componente é SVG inline
// 24x24 usando apenas stroke/fill transparentes, para que a cor venha via
// prop style.color ou className="text-cor". Usa forwardRef para não quebrar
// quem passa ref no ícone.

const LinkedInBrandIcon = forwardRef(function LinkedInBrandIcon(props, ref) {
  const { className, style, ...rest } = props;
  const cor = (style && style.color) || "currentColor";
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="LinkedIn"
      className={className}
      {...rest}
    >
      {/* Path oficial "in" com padding 3u em cada lado, centralizado 18x18 na viewBox 24. */}
      <g transform="translate(3 3) scale(0.75)">
        <path
          d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6 1.12 6 0 4.88 0 3.5 0 2.12 1.12 1 2.5 1c1.37 0 2.48 1.12 2.48 2.5zM0.22 8h4.56v14H0.22V8zM8.5 8h4.37v1.92h.06c.61-1.15 2.1-2.37 4.33-2.37C19.88 7.55 22 9.89 22 14.1V22h-4.56v-7.05c0-1.68-.03-3.84-2.34-3.84-2.35 0-2.71 1.83-2.71 3.71V22H7.82V8h.68z"
          fill={cor}
        />
      </g>
    </svg>
  );
});

const YouTubeBrandIcon = forwardRef(function YouTubeBrandIcon(props, ref) {
  const { className, style, ...rest } = props;
  const cor = (style && style.color) || "currentColor";
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="YouTube"
      className={className}
      {...rest}
    >
      {/* Caixa + play, padding 2u em cada lado para bater com os lucide. */}
      <rect
        x="2"
        y="6"
        width="20"
        height="12"
        rx="3.5"
        stroke={cor}
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M10.1 9.75a.3.3 0 0 0-.45.26v4a.3.3 0 0 0 .45.26l3.5-2a.3.3 0 0 0 0-.52l-3.5-2z"
        fill={cor}
      />
    </svg>
  );
});

const TikTokBrandIcon = forwardRef(function TikTokBrandIcon(props, ref) {
  const { className, style, ...rest } = props;
  const cor = (style && style.color) || "currentColor";
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="TikTok"
      className={className}
      {...rest}
    >
      {/* Nota oficial da TikTok, centralizada e com padding 2.5u igual os outros. */}
      <g transform="translate(3 2) scale(0.78)">
        <path
          d="M16.07 3.23a5.28 5.28 0 0 1-3.18-1.09 5.32 5.32 0 0 1-1.93-2.14h-.02V0H9.06v14.4a2.94 2.94 0 1 1-2.09-2.8V8.67a6.35 6.35 0 1 0 5.1 6.29v-2.15a3.57 3.57 0 0 1-3.02-3.5v-4.55h1.88c.14.95.55 1.82 1.17 2.49a5.3 5.3 0 0 0 3.97 1.83V6.18z"
          fill={cor}
        />
      </g>
    </svg>
  );
});

// As redes que chegam pela Zernio. O canal da conexão vem como
// "zernio:<rede>": um valor por rede fixado aqui obrigaria a mexer no código
// a cada rede nova que a Zernio passar a suportar, então o prefixo é que
// identifica a origem e o sufixo escolhe a aparência.
const REDES_ZERNIO = {
  instagram: {
    rotulo: "Instagram",
    icone: AtSign,
    classe: "bg-pink-500 text-white",
    classePill: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
    corSelo: "#E1306C",
  },
  facebook: {
    rotulo: "Messenger",
    icone: ThumbsUp,
    classe: "bg-sky-500 text-white",
    classePill: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
    corSelo: "#4267B2",
  },
  telegram: {
    rotulo: "Telegram",
    icone: Send,
    classe: "bg-cyan-500 text-white",
    classePill: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    corSelo: "#229ED9",
  },
  twitter: {
    rotulo: "X",
    icone: XIcon,
    classe: "bg-neutral-800 text-white",
    classePill: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
    corSelo: "#111111",
  },
  x: {
    rotulo: "X",
    icone: XIcon,
    classe: "bg-neutral-800 text-white",
    classePill: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
    corSelo: "#111111",
  },
  discord: {
    rotulo: "Discord",
    icone: Gamepad2,
    classe: "bg-indigo-500 text-white",
    classePill: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    corSelo: "#5865F2",
  },
  slack: {
    rotulo: "Slack",
    icone: Hash,
    classe: "bg-fuchsia-600 text-white",
    classePill: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400",
    corSelo: "#4A154B",
  },
  threads: {
    rotulo: "Threads",
    icone: MessageSquare,
    classe: "bg-neutral-700 text-white",
    classePill: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
    corSelo: "#000000",
  },
  tiktok: {
    rotulo: "TikTok",
    icone: TikTokBrandIcon,
    classe: "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900",
    classePill: "bg-neutral-900/10 text-neutral-900 dark:bg-white/10 dark:text-white",
    classeGlifo: "text-neutral-900 dark:text-white",
    corSelo: "#111111",
  },
  pinterest: {
    rotulo: "Pinterest",
    icone: Pin,
    classe: "bg-red-700 text-white",
    classePill: "bg-red-500/10 text-red-700 dark:text-red-400",
    corSelo: "#BD081C",
  },
  snapchat: {
    rotulo: "Snapchat",
    icone: Camera,
    classe: "bg-yellow-400 text-black",
    classePill: "bg-yellow-400/10 text-yellow-600 dark:text-yellow-400",
    corSelo: "#FFFC00",
  },
  reddit: {
    rotulo: "Reddit",
    icone: Ghost,
    classe: "bg-orange-500 text-white",
    classePill: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    corSelo: "#FF4500",
  },
  bluesky: {
    rotulo: "Bluesky",
    icone: Cloud,
    classe: "bg-blue-500 text-white",
    classePill: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    corSelo: "#0285FF",
  },
  whatsapp: {
    rotulo: "WhatsApp",
    icone: MessageCircle,
    classe: "bg-emerald-500 text-white",
    classePill: "bg-primary/10 text-primary",
    corSelo: "#25D366",
  },
  linkedin: {
    rotulo: "LinkedIn",
    icone: LinkedInBrandIcon,
    classe: "bg-sky-700 text-white",
    classePill: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
    corSelo: "#0A66C2",
  },
  youtube: {
    rotulo: "YouTube",
    icone: YouTubeBrandIcon,
    classe: "bg-red-600 text-white",
    classePill: "bg-red-500/10 text-red-600 dark:text-red-400",
    corSelo: "#FF0000",
  },
  googlebusiness: {
    rotulo: "Google Meu Negócio",
    icone: Star,
    classe: "bg-amber-500 text-white",
    classePill: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    corSelo: "#F4B400",
  },
};

// A identidade de uma rede: nome, glifo e cor. Fonte única — a tela da Zernio
// desenha as contas com ela e a lista de tickets marca a conversa com ela, e
// as duas precisam concordar sobre o que é "Messenger" e de que cor ele é.
//
// Rede que a Zernio passou a suportar e ainda não está no mapa não pode sair
// em branco nem virar "WhatsApp": devolve o próprio nome, em cinza.
export const identidadeDaRede = (plataforma) => {
  const rede = String(plataforma || "");
  const conhecida = REDES_ZERNIO[rede];

  return {
    chave: `zernio-${rede}`,
    rotulo: conhecida?.rotulo || rede.charAt(0).toUpperCase() + rede.slice(1),
    icone: conhecida?.icone || Share2,
    classe: conhecida?.classe || "bg-slate-500 text-white",
    classePill:
      conhecida?.classePill ||
      "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    corSelo: conhecida?.corSelo || "#64748B",
  };
};

// De onde a conversa veio. O que separa o WhatsApp oficial do não oficial é
// ter uma integração da Meta por trás: com ela é Cloud API, sem ela é a
// sessão do celular.
//
// CORREÇÃO DO FALLBACK PERIGOSO (piscada ícone):
// Antes usávamos `conexao?.channel || "whatsapp"` (linha antiga 180). O
// problema: quando o backend emite um evento "ticket" de update de status
// (ex: após "enviar mensagem", "assumir ticket", "transferir") ele às vezes
// não recarrega a relação "whatsapp" — ticket chega cru com .whatsapp =
// undefined. O OR caía direto em "whatsapp" e o ícone mudava de @ Rosa do
// Instagram para 🟢 WhatsApp VERDE por ~100 a 300ms, até chegar outro
// evento com ticket completo (includes whatsapp) e voltar o ícone
// correto de novo = PISCA-PISCA que o usuário reclamou.
//
// SOLUÇÃO DEFINITIVA: CACHE POR TICKET.ID. A primeira vez que renderizamos
// um ticket (no carregamento inicial da lista, API GET /tickets retorna
// completo com includes) gravamos o canal resolvido num WeakMap persistente.
// Em eventos posteriores de socket que chegarem com ticket CRU
// (sem .whatsapp, sem canal resolvível), retornamos o ÚLTIMO canal que
// o usuário JÁ VIU para aquele ticket — NUNCA MAIS cai em um canal
// ALEATÓRIO (whatsapp) por dados faltando no payload.
//
// Usa WeakMap para quando o objeto do ticket for coletado o cache sai
// junto, não causa memory leak.
const ultimoCanalPorTicket = new WeakMap();
const ultimoCanalPorTicketId = new Map();

export const canalDoTicket = (ticket) => {
  const conexao = ticket?.whatsapp;
  const conexaoDefinida = Boolean(conexao?.channel || conexao?.metaIntegrationId);

  // Se a propriedade de canal vem VAZIA (não resolvível neste payload) e
  // já temos um cache conhecido para este ticket, devolvemos o cache —
  // NÃO FALLBACK PARA WHATSAPP — evita a piscada.
  if (!conexaoDefinida) {
    const temObjNoCache = ticket && ultimoCanalPorTicket.has(ticket);
    const temIdNoCache = ticket?.id != null && ultimoCanalPorTicketId.has(String(ticket.id));
    const temCache = temObjNoCache || temIdNoCache;

    if (temCache) {
      return temObjNoCache
        ? ultimoCanalPorTicket.get(ticket)
        : ultimoCanalPorTicketId.get(String(ticket.id));
    }

    // Sem cache ainda E sem conexão definida: só agora usamos o fallback
    // "Desconhecido" em cinza, NÃO o WhatsApp. Porque o WhatsApp é um canal
    // ESPECÍFICO que confunde o usuário (ele sabe que aquela conversa não
    // é WhatsApp e fica pensando que tem problema). Ícone Share2 cinza
    // sinaliza "ainda carregando" — não um valor errado.
    return {
      chave: "indefinido",
      rotulo: "",
      icone: Share2,
      classe: "bg-slate-400 text-white",
      classePill: "bg-slate-400/10 text-slate-500 dark:text-slate-400",
      corSelo: "#94A3B8",
      ocultarPill: true, // Não renderiza nenhuma pill de canal enquanto
                         // não chegar o payload completo com includes
    };
  }

  // ===== Aqui em diante: conexão.channel DEFINIDA (payload completo). =====
  // Resolvemos o canal de verdade e GRAVAMOS no cache por ticket e id,
  // para próximos payloads incompletos não piscarem mais.
  const canal = conexao.channel;
  let identidade;

  if (canal.startsWith("zernio:")) {
    identidade = identidadeDaRede(canal.slice("zernio:".length));
  } else if (canal === "facebook") {
    identidade = {
      chave: "facebook",
      rotulo: "Facebook",
      icone: ThumbsUp,
      classe: "bg-sky-500 text-white",
      classePill: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
      corSelo: "#4267B2",
    };
  } else if (canal === "instagram") {
    identidade = {
      chave: "instagram",
      rotulo: "Instagram",
      icone: AtSign,
      classe: "bg-pink-500 text-white",
      classePill: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
      corSelo: "#E1306C",
    };
  } else if (canal === "telegram") {
    identidade = {
      chave: "telegram",
      rotulo: "Telegram",
      icone: Send,
      classe: "bg-cyan-500 text-white",
      classePill: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
      corSelo: "#229ED9",
    };
  } else if (canal === "webchat") {
    identidade = {
      chave: "webchat",
      rotulo: "Chat do site",
      icone: Globe,
      classe: "bg-indigo-500 text-white",
      classePill: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
      corSelo: "#6366F1",
    };
  } else if (canal === "whatsapp_official" || conexao?.metaIntegrationId) {
    identidade = {
      chave: "whatsapp-oficial",
      rotulo: "WhatsApp Oficial",
      icone: BadgeCheck,
      classe: "bg-emerald-600 text-white",
      classePill: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      corSelo: "#128C7E",
    };
  } else {
    // Canal válido + conhecido (whatsapp normal)
    identidade = {
      chave: "whatsapp",
      rotulo: "WhatsApp",
      icone: MessageCircle,
      classe: "bg-emerald-500 text-white",
      classePill: "bg-primary/10 text-primary",
      corSelo: "#25D366",
    };
  }

  // Cache persistente por ticket objeto e por id numérico (dupla entrada
  // porque às vezes o reducer do frontend substitui a referência e o
  // WeakMap perde, mas por String(id) a gente retém sempre).
  try { if (ticket) ultimoCanalPorTicket.set(ticket, identidade); } catch {}
  if (ticket?.id != null) {
    ultimoCanalPorTicketId.set(String(ticket.id), identidade);
  }

  return identidade;
};

// O nome da rede a partir do canal da conexão, sem precisar de um ticket.
//
// A tela de Conexões mostrava só o nome da conta — "Automação e Sistemas💻",
// "Webba" — e nada dizia de qual rede era cada uma. Quem tem Facebook e
// Instagram conectados via duas linhas parecidas e um ícone de 16px como
// única diferença; a conta de anúncios, que trazia "(metaads)" no nome, era a
// única que se lia como rede. Aqui o rótulo vira texto.
export const rotuloDoCanal = (channel) => {
  const canal = String(channel || "whatsapp");

  if (canal.startsWith("zernio:")) {
    return identidadeDaRede(canal.slice("zernio:".length)).rotulo;
  }

  const fixos = {
    whatsapp: "WhatsApp",
    whatsapp_official: "WhatsApp Oficial",
    facebook: "Facebook",
    instagram: "Instagram",
    telegram: "Telegram",
    webchat: "Chat do site",
  };

  return fixos[canal] || canal;
};

export default canalDoTicket;
